'use client'
// ═══════════════════════════════════════════════════════════════════════════
// « VOIR COMME LE CLIENT » — l'admin garde SA session.
//
// Pourquoi ce module existe (12/08/2026, demande fondateur) :
// `impersonation.js` ouvre une VRAIE session au nom de la cible
// (`supabase.auth.verifyOtp`). Supabase horodate alors `last_sign_in_at` sur le
// compte du client — exactement comme si le client s'était connecté lui-même.
// Conséquence mesurée sur KAP Group : le compte affichait une connexion « hier
// à 11h51 » qui était en réalité celle du fondateur en train de tester. Cette
// date ne dit donc plus RIEN de l'adoption réelle : impossible de savoir quel
// client a ouvert l'app une seule fois. Le fondateur, le 12/08 : « je dois
// vraiment savoir quand eux se connectent, pas quand moi je rentre sur leur app ».
//
// Ici, RIEN n'est écrit sur le compte du client :
//   • la session reste celle de l'admin (aucun token échangé) ;
//   • seul l'AFFICHAGE change — le menu et les écrans suivent le rôle simulé ;
//   • le dossier client, lui, passe par le sélecteur existant (SELECT_CLIENT),
//     source de vérité unique (cf. active-client-resolver.js).
//
// ⚠️ CE MODE CHANGE CE QUI EST AFFICHÉ, PAS LES DROITS SERVEUR.
// Les appels API partent toujours avec le jeton de l'admin, qui garde tous ses
// droits. C'est VOULU : ce qui est fait en « voir comme » reste tracé sous
// l'identité réelle de l'admin, jamais sous celle du client. Ne jamais présenter
// ce mode comme un bac à sable étanche.
// ═══════════════════════════════════════════════════════════════════════════

const KEY = 'aureus_view_as'

// Rôles simulables. Tous STRICTEMENT plus restreints que 'admin' : le mode ne
// peut donc jamais élargir ce qu'on voit. 'admin' est volontairement absent.
export const ROLES_SIMULABLES = ['employeur', 'rh_entreprise', 'employe', 'comptable']

/** État courant, ou null. Lisible côté client uniquement. */
export function getViewAs() {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const v = JSON.parse(raw)
    // Un rôle hors liste (clé bricolée à la main) ne s'applique pas.
    return ROLES_SIMULABLES.includes(v?.role) ? v : null
  } catch { return null }
}

/**
 * Active le mode. À n'appeler QUE depuis un écran admin — le garde-fou qui
 * compte est côté layout (`realRole === 'admin'`), celui-ci n'est que le premier.
 * @param {{role: string, clientId?: string, clientNom?: string}} opts
 */
export function startViewAs({ role, clientId = null, clientNom = '' }) {
  if (typeof window === 'undefined') return
  if (!ROLES_SIMULABLES.includes(role)) throw new Error(`Rôle non simulable : ${role}`)
  sessionStorage.setItem(KEY, JSON.stringify({ role, clientId, clientNom, depuis: new Date().toISOString() }))
}

/** Coupe le mode. Ne touche ni à la session, ni au client sélectionné. */
export function stopViewAs() {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(KEY)
}

/**
 * Rôle à utiliser pour l'AFFICHAGE.
 * Le mode ne s'applique que si l'utilisateur RÉEL est admin : un employeur qui
 * poserait la clé à la main ne gagne rien (il ne peut de toute façon simuler
 * qu'un rôle plus restreint, et le serveur ne le croit pas sur parole).
 */
export function roleAffiche(realRole, viewAs) {
  if (realRole !== 'admin') return realRole
  return viewAs?.role || realRole
}
